import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmailCampaignService } from "../../services/EmailCampaignService";
import {
  CampaignFormData,
  TargetGroup,
  SendType,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  MerchantEmail
} from "../../types/EmailCampaignTypes";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CustomButton from "../../components/CommonButton/CommonButton";
import {
  Box,
  Typography,
  Alert,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import { Tabs, Tab } from "@mui/material";
import Loader from "../../components/loader/loader";
import "./CreateEmailCampaign.css"; // Reusing the same styles
import EmailContentEditor from "../../components/EmailContentEditor/EmailContentEditor";
import { useAppSelector } from "../../redux/hooks";
import FileUploadPreview, {
  UploadedFile
} from "../../components/FileUploadPreview/FileUploadPreview";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
// DateTimePicker removed; using separate date and AM/PM time select

const EditEmailCampaign: React.FC = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();

  // Get business details from localStorage
  const [businessData, setBusinessData] = useState<any>(null);
  const [businessToken, setBusinessToken] = useState<string | null>(null);

  const [formData, setFormData] = useState<CampaignFormData>({
    emailSubject: "",
    emailContent: "",
    campaignName: "",
    targetGroupId: 0,
    sendType: SendType.SendNow,
    sendOutDate: "",
    sendOutTime: "",
    testEmails: [""]
  });

  const [testEmail, setTestEmail] = useState("");

  const [originalCampaign, setOriginalCampaign] =
    useState<MerchantEmail | null>(null);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [totalRecipients, setTotalRecipients] = useState(0);
  const [subscribersLoading, setSubscribersLoading] = useState<boolean>(false);
  const [recipientsLoading, setRecipientsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testEmailResult, setTestEmailResult] = useState<string | null>(null);
  const [contentTab, setContentTab] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Local 12h time parts for AM/PM picker
  const [timeHour12, setTimeHour12] = useState<string>("");
  const [timeMinute, setTimeMinute] = useState<string>("");
  const [timePeriod, setTimePeriod] = useState<"AM" | "PM" | "">("");
  const [timeMenuOpen, setTimeMenuOpen] = useState<boolean>(false);
  const [timeMenuAnchor, setTimeMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  // Global billing / restriction state from Redux
  const { isNonPaying, isBillingLoading } = useAppSelector(
    (s) => s.businessAccess
  );

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        if (file.preview && !file.url) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.sendOutTime) {
      const [h, m] = formData.sendOutTime.split(":");
      const hourNum = parseInt(h, 10);
      const period = hourNum >= 12 ? "PM" : "AM";
      const hour12 = (hourNum % 12 || 12).toString().padStart(2, "0");
      setTimeHour12(hour12);
      setTimeMinute(m);
      setTimePeriod(period);
    } else {
      setTimeHour12("");
      setTimeMinute("");
      setTimePeriod("");
    }
  }, [formData.sendOutTime]);

  const hours12 = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );
  const minutes5 = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0")
  );
  const periods: Array<"AM" | "PM"> = ["AM", "PM"];

  const to24h = (h12: string, m: string, period: "AM" | "PM"): string => {
    const h = parseInt(h12, 10);
    let h24 = h % 12;
    if (period === "PM") h24 += 12;
    const hh = String(h24).padStart(2, "0");
    return `${hh}:${m}`;
  };

  const handleTimePartChange = (
    part: "hour" | "minute" | "period",
    value: string
  ) => {
    let nextHour = timeHour12;
    let nextMinute = timeMinute;
    let nextPeriod = timePeriod;
    if (part === "hour") nextHour = value;
    if (part === "minute") nextMinute = value;
    if (part === "period") nextPeriod = value as "AM" | "PM";

    setTimeHour12(nextHour);
    setTimeMinute(nextMinute);
    setTimePeriod(nextPeriod);

    if (nextHour && nextMinute && nextPeriod) {
      const hhmm = to24h(nextHour, nextMinute, nextPeriod);
      setFormData((prev) => ({ ...prev, sendOutTime: hhmm }));
      
      // Validate if the selected time is in the past
      if (isTodaySelected()) {
        const selectedDateTime = new Date();
        selectedDateTime.setHours(
          nextPeriod === "PM" && parseInt(nextHour, 10) !== 12 
            ? parseInt(nextHour, 10) + 12 
            : nextPeriod === "AM" && parseInt(nextHour, 10) === 12 
            ? 0 
            : parseInt(nextHour, 10)
        );
        selectedDateTime.setMinutes(parseInt(nextMinute, 10));
        selectedDateTime.setSeconds(0);
        selectedDateTime.setMilliseconds(0);
        
        const now = new Date();
        if (selectedDateTime <= now) {
          setError("Please select a future time. The selected time is in the past.");
        } else {
          setError(null);
        }
      }
    }
  };

  const handleTimeInputClick = (e: React.MouseEvent<HTMLElement>) => {
    setTimeMenuAnchor(e.currentTarget as HTMLElement);
    setTimeMenuOpen(true);
  };

  const closeTimeMenuIfComplete = () => {
    if (timeHour12 && timeMinute && timePeriod) {
      setTimeMenuOpen(false);
    }
  };

  const isTodaySelected = () => {
    const todayStr = new Date().toISOString().split("T")[0];
    return formData.sendOutDate === todayStr;
  };

  const isPeriodDisabled = (period: "AM" | "PM") => {
    if (!isTodaySelected()) return false;
    const now = new Date();
    const nowPeriod: "AM" | "PM" = now.getHours() >= 12 ? "PM" : "AM";
    // Disable AM if it's already PM today
    return period === "AM" && nowPeriod === "PM";
  };

  const isHourDisabled = (h12: string, period: "AM" | "PM") => {
    if (!isTodaySelected()) return false;
    const now = new Date();
    const nowPeriod: "AM" | "PM" = now.getHours() >= 12 ? "PM" : "AM";
    const nowHour12 = (now.getHours() % 12 || 12).toString().padStart(2, "0");
    
    // If selecting AM but it's already PM, disable all AM hours
    if (period === "AM" && nowPeriod === "PM") return true;
    
    // If different periods, don't disable
    if (period !== nowPeriod) return false;
    
    // Same period: disable hours less than current hour
    const selectedHour = parseInt(h12, 10);
    const currentHour = parseInt(nowHour12, 10);
    return selectedHour < currentHour;
  };

  const isMinuteDisabled = (m: string) => {
    if (!isTodaySelected()) return false;
    if (!timeHour12 || !timePeriod) return false;
    
    const now = new Date();
    const nowPeriod: "AM" | "PM" = now.getHours() >= 12 ? "PM" : "AM";
    const nowHour12 = (now.getHours() % 12 || 12).toString().padStart(2, "0");
    
    // If selected period is AM but current is PM, disable all minutes
    if (timePeriod === "AM" && nowPeriod === "PM") return true;
    
    // If different periods, don't disable
    if (timePeriod !== nowPeriod) return false;
    
    const selectedHour = parseInt(timeHour12, 10);
    const currentHour = parseInt(nowHour12, 10);
    
    // If selected hour is less than current hour, disable all minutes
    if (selectedHour < currentHour) return true;
    
    // If same hour and same period, disable past and current minutes (add buffer)
    if (selectedHour === currentHour) {
      const selectedMinute = parseInt(m, 10);
      const currentMinute = now.getMinutes();
      // Disable if the minute is less than or equal to current minute
      return selectedMinute <= currentMinute;
    }
    
    return false;
  };

  // Initialize business data from localStorage
  useEffect(() => {
    const currentBusiness = localStorage.getItem("currentBusiness");
    const token = localStorage.getItem("businessAccessToken");

    if (currentBusiness) {
      setBusinessData(JSON.parse(currentBusiness));
    }
    setBusinessToken(token);
  }, []);

  const loadCampaignData = useCallback(async () => {
    if (!businessData?.BusinessId || !businessToken || !campaignId) return;

    try {
      setInitialLoading(true);
      setSubscribersLoading(true);
      setRecipientsLoading(true);
      const emailCampaignService = new EmailCampaignService();

      const [campaign, groups, totalCount] = await Promise.all([
        emailCampaignService.getMerchantEmailByCampaignId(campaignId),
        emailCampaignService.getEmailCampaignTargetGroups(
          businessData.BusinessId
        ),
        emailCampaignService.getTotalNoOfRecipients(businessData.BusinessId)
      ]);

      setOriginalCampaign(campaign);
      setTargetGroups(groups);
      setTotalSubscribers(totalCount.Count);

      // Parse UTC datetime from backend and convert to local for form display
      const sendDate = new Date(campaign.SendOutDateTime);
      const sendOutDate = sendDate.toISOString().split("T")[0];
      const sendOutTime = sendDate.toTimeString().slice(0, 5);

      // Determine send type based on date
      const now = new Date();
      const sendType = sendDate > now ? SendType.Schedule : SendType.SendNow;

      setFormData({
        emailSubject: campaign.EmailSubject || "",
        emailContent: campaign.EmailContent || "",
        campaignName: campaign.CampaignName || "",
        targetGroupId: campaign.TargetGroupsId,
        sendType: sendType,
        sendOutDate: sendType === SendType.Schedule ? sendOutDate : "",
        sendOutTime: sendType === SendType.Schedule ? sendOutTime : "",
        testEmails: [""]
      });

      // Load recipient count for selected target group
      if (
        campaign.TargetGroupsId &&
        campaign.TargetGroupsId !== 0 &&
        campaign.TargetGroupsId !== 7
      ) {
        try {
          setRecipientsLoading(true);
          const result = await emailCampaignService.getPatronListCount({
            BusinessId: businessData.BusinessId,
            GroupId: campaign.TargetGroupsId
          });
          setTotalRecipients(result.Count);
        } catch (error) {
          setTotalRecipients(totalCount.Count);
        } finally {
          setRecipientsLoading(false);
        }
      } else {
        setTotalRecipients(totalCount.Count);
        setRecipientsLoading(false);
      }
    } catch (error) {
      console.error("Error loading campaign data:", error);
      setError("Failed to load campaign data. Please try again.");
    } finally {
      setInitialLoading(false);
      setSubscribersLoading(false);
    }
  }, [businessData?.BusinessId, businessToken, campaignId]);

  useEffect(() => {
    if (campaignId && businessData?.BusinessId && businessToken) {
      loadCampaignData();
    }
  }, [campaignId, businessData?.BusinessId, businessToken, loadCampaignData]);

  // Auto-scroll to error when it appears (with offset for fixed headers)
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        const element = errorRef.current as HTMLDivElement;
        const headerOffset = 120;
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        const scrollTarget = Math.max(elementTop - headerOffset, 0);
        window.scrollTo({ top: scrollTarget, behavior: "smooth" });
        setTimeout(() => element.focus({ preventScroll: true }), 200);
      }, 0);
    }
  }, [error]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTargetGroupChange = async (
    e: React.ChangeEvent<{ value: unknown }>
  ) => {
    const groupId = parseInt(e.target.value as string);
    setFormData((prev) => ({ ...prev, targetGroupId: groupId }));

    if (groupId && groupId !== 0 && groupId !== 7) {
      try {
        setRecipientsLoading(true);
        const emailCampaignService = new EmailCampaignService();
        const result = await emailCampaignService.getPatronListCount({
          BusinessId: businessData.BusinessId,
          GroupId: groupId
        });

        if (result.Count > 0) {
          setTotalRecipients(result.Count);
        } else {
          setTotalRecipients(0);
          setError(
            "No patrons found in the selected group. Please select another group."
          );
        }
      } catch (error) {
        console.error("Error getting patron count:", error);
        setError("Failed to get patron count for selected group.");
      } finally {
        setRecipientsLoading(false);
      }
    } else {
      setTotalRecipients(totalSubscribers);
      setRecipientsLoading(false);
    }
  };

  const handleSendTypeChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const sendType = parseInt(e.target.value as string);
    setFormData((prev) => ({ ...prev, sendType }));
  };

  const handleContentTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setContentTab(newValue);
  };

  const handleImageInsert = (imageUrl: string) => {
    const quill = (window as any).quillInstance;
    if (quill) {
      try {
        const range = quill.getSelection();
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, "image", imageUrl, "user");
        quill.setSelection(index + 1);
      } catch (error) {
        console.error("Error inserting image into Quill:", error);
        const currentContent = formData.emailContent;
        const imageHtml = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
        const newContent = currentContent
          ? `${currentContent}<br/>${imageHtml}`
          : imageHtml;
        setFormData((prev) => ({ ...prev, emailContent: newContent }));
      }
    } else {
      const currentContent = formData.emailContent;
      const imageHtml = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
      const newContent = currentContent
        ? `${currentContent}<br/>${imageHtml}`
        : imageHtml;
      setFormData((prev) => ({ ...prev, emailContent: newContent }));
    }
  };

  const validateForm = (): boolean => {
    if (!(formData.campaignName || "").trim()) {
      setError("Campaign name is required.");
      return false;
    }
    if (!(formData.emailSubject || "").trim()) {
      setError("Email subject is required.");
      return false;
    }
    if (!(formData.emailContent || "").trim()) {
      setError("Email content is required.");
      return false;
    }
    if (!formData.targetGroupId) {
      setError("Please select a target group.");
      return false;
    }
    if (formData.sendType === SendType.Schedule && !formData.sendOutDate) {
      setError("Please select a send date for scheduled campaigns.");
      return false;
    }
    if (formData.sendType === SendType.Schedule && !formData.sendOutTime) {
      setError("Please select a send time for scheduled campaigns.");
      return false;
    }
    if (
      formData.sendType === SendType.Schedule &&
      formData.sendOutDate &&
      formData.sendOutTime
    ) {
      const selectedDateTime = new Date(
        `${formData.sendOutDate}T${formData.sendOutTime}:00`
      );
      const now = new Date();
      if (selectedDateTime.getTime() < now.getTime()) {
        setError("Scheduled time cannot be in the past.");
        return false;
      }
    }
    return true;
  };

  const handleSendTest = async () => {
    if (!(formData.emailSubject || "").trim() || !(formData.emailContent || "").trim()) {
      setError(
        "Please fill in the email subject and content before sending a test."
      );
      return;
    }

    if (!testEmail.trim() || !isValidEmail(testEmail)) {
      setError("Please enter a valid email address for testing.");
      return;
    }

    try {
      setLoading(true);
      setTestEmailResult(null);
      setError(null);

      const emailCampaignService = new EmailCampaignService();
      await emailCampaignService.sendTestCampaign({
        BusinessId: businessData.BusinessId,
        CampaignId: campaignId,
        EmailSubject: formData.emailSubject,
        EmailContent: formData.emailContent,
        TestEmails: [testEmail]
      });

      setTestEmailResult("Test email sent successfully!");
    } catch (error) {
      console.error("Error sending test email:", error);
      setTestEmailResult("Failed to send test email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !campaignId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const emailCampaignService = new EmailCampaignService();

      const request: UpdateCampaignRequest = {
        BusinessId: businessData.BusinessId,
        CampaignId: campaignId,
        EmailSubject: formData.emailSubject,
        EmailContent: formData.emailContent,
        CampaignName: formData.campaignName,
        TargetGroupsId: formData.targetGroupId,
        SendType: formData.sendType
      };

      const response = await emailCampaignService.updateCampaign(
        request,
        formData.sendType === SendType.Schedule ? formData.sendOutDate : undefined,
        formData.sendType === SendType.Schedule ? formData.sendOutTime : undefined
      );

      if (response.Success) {
        setSuccess("Campaign updated successfully!");
        navigate("/emailcampaigns");
      } else {
        setError(response.Message || "Failed to update campaign.");
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
      setError("Failed to update campaign. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/emailcampaigns");
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation functions for button disabled states
  const isFormValid = (): boolean => {
    return (
      (formData.campaignName || "").trim() !== "" &&
      (formData.emailSubject || "").trim() !== "" &&
      (formData.emailContent || "").trim() !== "" &&
      formData.targetGroupId > 0 &&
      (formData.sendType === SendType.SendNow ||
        (formData.sendType === SendType.Schedule &&
          Boolean(formData.sendOutDate) &&
          Boolean(formData.sendOutTime)))
    );
  };

  const isTestEmailValid = (): boolean => {
    return testEmail.trim() !== "" && isValidEmail(testEmail);
  };

  const isScheduleValid = (): boolean => {
    if (formData.sendType !== SendType.Schedule) return true;

    if (!formData.sendOutDate || !formData.sendOutTime) return false;

    const selectedDateTime = new Date(
      `${formData.sendOutDate}T${formData.sendOutTime}:00`
    );
    const now = new Date();
    return selectedDateTime.getTime() > now.getTime();
  };

  if (!originalCampaign) {
    return (
      <CommonPageLayout heading="Edit Email Campaign" subHeading="" docs={null}>
        {initialLoading ? (
          <Loader />
        ) : (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              Campaign not found or failed to load.
            </Alert>
            <CustomButton variant="outlined" onClick={handleCancel}>
              Back to Campaigns
            </CustomButton>
          </Box>
        )}
      </CommonPageLayout>
    );
  }

  return (
    <CommonPageLayout
      heading="Edit Email Campaign"
      subHeading=""
      docs={
        <CustomButton
          variant="outlined"
          onClick={() => navigate("/emailcampaigns")}
        >
          Back to Campaigns
        </CustomButton>
      }
    >
      <Box>
        {isNonPaying && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">
              Access limited: Please renew your contract or fix payment issues
              to edit email campaigns.
            </Alert>
          </Box>
        )}

        {error && (
          <div ref={errorRef} tabIndex={-1} aria-live="assertive">
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          </div>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 1 }}
            >
              Total Number of Subscribers
            </Typography>
            <Typography
              sx={{
                margin: "0px",
                fontFamily: "Montserrat, sans-serif",
                color: "#192f67",
                fontSize: "24px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "normal",
                textTransform: "capitalize"
              }}
            >
              {subscribersLoading ? (
                <CircularProgress size={28} sx={{ color: "#192f67" }} />
              ) : (
                totalSubscribers.toLocaleString()
              )}
            </Typography>
          </CardContent>
        </Card>

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              Which customers would you like to target with this campaign
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the group of customers.
            </Typography>
            <TextField
              select
              name="targetGroupId"
              value={formData.targetGroupId}
              onChange={handleTargetGroupChange}
              fullWidth
              required
              label="Select customer group"
              sx={{ mb: 3 }}
            >
              <MenuItem value="">Select customer group</MenuItem>
              {targetGroups.map((group) => (
                <MenuItem key={group.Id} value={group.Id}>
                  {group.TargetGroup}
                </MenuItem>
              ))}
            </TextField>

            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#192F67", mb: 1 }}
                >
                  Total Number of Recipients
                </Typography>
                <Typography
                  sx={{
                    margin: "0px",
                    fontFamily: "Montserrat, sans-serif",
                    color: "#192f67",
                    fontSize: "24px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "normal",
                    textTransform: "capitalize"
                  }}
                >
                  {recipientsLoading ? (
                    <CircularProgress size={28} sx={{ color: "#192f67" }} />
                  ) : (
                    totalRecipients.toLocaleString()
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              Campaign Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide the campaign details below.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#192F67", mb: 1, fontSize: "1rem" }}
              >
                Campaign Name *
              </Typography>
              <TextField
                name="campaignName"
                value={formData.campaignName}
                onChange={handleInputChange}
                placeholder="Enter campaign name"
                fullWidth
                required
                label="Campaign Name"
                variant="outlined"
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#192F67", mb: 1, fontSize: "1rem" }}
              >
                Email Subject *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This will appear as the subject line in your email.
              </Typography>
              <TextField
                name="emailSubject"
                value={formData.emailSubject}
                onChange={handleInputChange}
                placeholder="Enter email subject"
                fullWidth
                required
                label="Email Subject"
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              What content do you want to put in this email?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create, edit and customise what you want to put in this email.
            </Typography>

            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                backgroundColor: "#fafafa",
                overflow: "hidden"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#fff",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2
                }}
              >
                <Tabs
                  value={contentTab}
                  onChange={handleContentTabChange}
                  aria-label="Content mode tabs"
                  variant="fullWidth"
                  sx={{
                    flex: 1,
                    minHeight: 36,
                    "& .MuiTabs-indicator": {
                      height: 0
                    },
                    "& .MuiTab-root": {
                      minHeight: 36,
                      padding: "6px 12px"
                    },
                    "& .MuiTab-iconWrapper": {
                      color: "inherit"
                    }
                  }}
                >
                  <Tab
                    icon={<EditIcon sx={{ fontSize: 20 }} />}
                    iconPosition="start"
                    label="Design"
                    sx={{
                      backgroundColor:
                        contentTab === 0
                          ? "#223354 !important"
                          : "#f5f5f5 !important",
                      color:
                        contentTab === 0
                          ? "#fff !important"
                          : "#666 !important",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "14px",
                      lineHeight: 1.2,
                      "&:hover": {
                        backgroundColor:
                          contentTab === 0 ? "#1976d2" : "#e0e0e0"
                      }
                    }}
                  />
                  <Tab
                    icon={<CodeIcon sx={{ fontSize: 20 }} />}
                    iconPosition="start"
                    label="HTML"
                    sx={{
                      backgroundColor:
                        contentTab === 1
                          ? "#223354 !important"
                          : "#f5f5f5 !important",
                      color:
                        contentTab === 1
                          ? "#fff !important"
                          : "#666 !important",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "14px",
                      lineHeight: 1.2,
                      "&:hover": {
                        backgroundColor:
                          contentTab === 1 ? "#1976d2" : "#e0e0e0"
                      }
                    }}
                  />
                </Tabs>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CustomButton
                    variant="outlined"
                    // startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadModalOpen(true)}
                    sx={{
                      borderColor: "#192F67",
                      color: "#192F67",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "#192F67",
                        backgroundColor: "rgba(25, 47, 103, 0.04)"
                      }
                    }}
                  >
                    Upload Images
                  </CustomButton>
                  {/* {uploadedFiles.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {uploadedFiles.length} uploaded
                    </Typography>
                  )} */}
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  minHeight: "400px",
                  position: "relative"
                }}
              >
                {contentTab === 0 ? (
                  <Box
                    sx={{
                      height: "400px",
                      overflow: "auto",
                      "& .ql-container": {
                        border: "none",
                        height: "calc(100% - 42px)"
                      },
                      "& .ql-toolbar": {
                        border: "none",
                        borderBottom: "1px solid #e0e0e0"
                      },
                      "& .ql-editor": {
                        overflowY: "auto"
                      }
                    }}
                  >
                    <EmailContentEditor
                      id="email-content-editor-edit"
                      value={formData.emailContent}
                      onChange={(html) =>
                        setFormData((prev) => ({ ...prev, emailContent: html }))
                      }
                      placeholder="Write your email content here..."
                    />
                  </Box>
                ) : (
                  <TextField
                    name="emailContent"
                    value={formData.emailContent}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        emailContent: e.target.value
                      }))
                    }
                    placeholder="Paste or write HTML here..."
                    fullWidth
                    multiline
                    rows={16}
                    variant="outlined"
                    sx={{
                      height: "400px",
                      "& .MuiOutlinedInput-root": {
                        border: "none",
                        height: "100%",
                        "& fieldset": {
                          border: "none"
                        }
                      },
                      "& .MuiInputBase-input": {
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                        fontSize: "14px",
                        lineHeight: 1.5,
                        overflowY: "auto !important"
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              When would you like the email to be sent out?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Would you like to schedule or send the message now?
            </Typography>
            <TextField
              select
              name="sendType"
              value={formData.sendType}
              onChange={handleSendTypeChange}
              fullWidth
              required
              label="Select send type"
              sx={{ mb: 3 }}
            >
              <MenuItem value="">Select send type</MenuItem>
              <MenuItem value={SendType.SendNow}>Send Now</MenuItem>
              <MenuItem value={SendType.Schedule}>Schedule</MenuItem>
            </TextField>

            {formData.sendType === SendType.Schedule && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Choose the date and time you wish the email to be sent?
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    type="date"
                    name="sendOutDate"
                    value={formData.sendOutDate}
                    onChange={handleInputChange}
                    label="Send out date"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split("T")[0] }}
                    required
                    sx={{ flex: 1, minWidth: "200px" }}
                  />
                  <Box sx={{ flex: 1, minWidth: "320px" }}>
                    <TextField
                      label="Send out time"
                      value={
                        timeHour12 && timeMinute && timePeriod
                          ? `${timeHour12}:${timeMinute} ${timePeriod}`
                          : ""
                      }
                      onClick={handleTimeInputClick}
                      inputProps={{ readOnly: true }}
                      placeholder="Select time"
                      fullWidth
                    />
                    <Menu
                      anchorEl={timeMenuAnchor}
                      open={timeMenuOpen}
                      onClose={() => setTimeMenuOpen(false)}
                      MenuListProps={{ dense: true }}
                      PaperProps={{ sx: { p: 1 } }}
                    >
                      <Box sx={{ display: "flex", px: 1 }}>
                        <Box sx={{ maxHeight: 240, overflowY: "auto", pr: 1 }}>
                          {hours12.map((h) => (
                            <MenuItem
                              key={h}
                              disabled={isHourDisabled(h, timePeriod || "AM")}
                              selected={timeHour12 === h}
                              onClick={() => {
                                handleTimePartChange("hour", h);
                                closeTimeMenuIfComplete();
                              }}
                            >
                              {h}
                            </MenuItem>
                          ))}
                        </Box>
                        <Box sx={{ maxHeight: 240, overflowY: "auto", pr: 1 }}>
                          {minutes5.map((m) => (
                            <MenuItem
                              key={m}
                              disabled={isMinuteDisabled(m)}
                              selected={timeMinute === m}
                              onClick={() => {
                                handleTimePartChange("minute", m);
                                closeTimeMenuIfComplete();
                              }}
                            >
                              {m}
                            </MenuItem>
                          ))}
                        </Box>
                        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                          {periods.map((p) => (
                            <MenuItem
                              key={p}
                              disabled={isPeriodDisabled(p)}
                              selected={timePeriod === p}
                              onClick={() => {
                                handleTimePartChange("period", p);
                                closeTimeMenuIfComplete();
                              }}
                            >
                              {p}
                            </MenuItem>
                          ))}
                        </Box>
                      </Box>
                    </Menu>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
            >
              Finalise campaign
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can test this email by entering your email address and
              pressing test. If you think everything is looking as it should, or
              you live dangerously and don't want to test. Just go on ahead and
              press create.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#192F67", mb: 2 }}
              >
                Test Email Addresses
              </Typography>
              <Box
                sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3 }}
              >
                <TextField
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Email Address"
                  label="Test Email"
                  sx={{ flex: 1 }}
                />
                <CustomButton
                  variant="contained"
                  onClick={handleSendTest}
                  disabled={
                    loading ||
                    !isTestEmailValid() ||
                    !(formData.emailSubject || "").trim() ||
                    !(formData.emailContent || "").trim()
                  }
                  sx={{ minWidth: "120px", height: "40px" }}
                >
                  {loading ? "Sending..." : "Send Test"}
                </CustomButton>
              </Box>

              {testEmailResult && (
                <Alert
                  severity={
                    testEmailResult.includes("success") ? "success" : "error"
                  }
                  sx={{ mb: 2 }}
                >
                  {testEmailResult}
                </Alert>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <CustomButton
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              variant="contained"
              className="blue_button"
              disabled={loading || !isFormValid() || !isScheduleValid() || isNonPaying || isBillingLoading}
            >
              {loading ? "Updating..." : "Update Campaign"}
            </CustomButton>
          </Box>
        </Box>
      </Box>

      {/* File Upload Modal */}
      <Dialog
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            height: "auto"
          }
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            pb: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#192F67" }}>
            Upload Images
          </Typography>
          <IconButton
            onClick={() => setUploadModalOpen(false)}
            sx={{ color: "#666" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Upload PNG, JPEG or JPG images to use in your email. Image size should be less than 10MB. Select an image and
            click the insert icon to add it directly into your content.
          </Typography>
          <FileUploadPreview
            uploadedFiles={uploadedFiles}
            onFilesChange={setUploadedFiles}
            businessId={businessData?.BusinessId}
            onImageInsert={(url) => {
              handleImageInsert(url);
              setUploadModalOpen(false);
            }}
            maxFiles={10}
            maxFileSizeMB={10}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <CustomButton
            variant="outlined"
            onClick={() => setUploadModalOpen(false)}
            sx={{
              borderColor: "#192F67",
              color: "#192F67",
              "&:hover": {
                borderColor: "#192F67",
                backgroundColor: "rgba(25, 47, 103, 0.04)"
              }
            }}
          >
            Close
          </CustomButton>
        </DialogActions>
      </Dialog>
    </CommonPageLayout>
  );
};

export default EditEmailCampaign;
